/** Parsed contact row for review display */
export interface ParsedContactPreview {
  firstName?: string
  lastName?: string
  email?: string
  phoneNumber?: string
  organizationName?: string
  notes?: string
  ordoUserId?: string
  timezone?: string
  extraData?: Record<string, string>
}

// ── Header normalization ──

const HEADER_ALIASES: Record<string, string> = {
  first_name: 'firstName',
  firstname: 'firstName',
  'first name': 'firstName',
  fname: 'firstName',
  'given name': 'firstName',
  givenname: 'firstName',
  last_name: 'lastName',
  lastname: 'lastName',
  'last name': 'lastName',
  lname: 'lastName',
  surname: 'lastName',
  'family name': 'lastName',
  familyname: 'lastName',
  name: 'fullName',
  'full name': 'fullName',
  fullname: 'fullName',
  'contact name': 'fullName',
  contactname: 'fullName',
  email: 'email',
  'email address': 'email',
  emailaddress: 'email',
  'e-mail': 'email',
  phone_number: 'phoneNumber',
  phonenumber: 'phoneNumber',
  phone: 'phoneNumber',
  'phone number': 'phoneNumber',
  'phone no': 'phoneNumber',
  'phone no.': 'phoneNumber',
  'phone #': 'phoneNumber',
  telephone: 'phoneNumber',
  tel: 'phoneNumber',
  mobile: 'phoneNumber',
  'mobile phone': 'phoneNumber',
  mobilephone: 'phoneNumber',
  'mobile number': 'phoneNumber',
  cell: 'phoneNumber',
  'cell phone': 'phoneNumber',
  cellphone: 'phoneNumber',
  'cell number': 'phoneNumber',
  organization_name: 'organizationName',
  organizationname: 'organizationName',
  org: 'organizationName',
  organization: 'organizationName',
  company: 'organizationName',
  'company name': 'organizationName',
  companyname: 'organizationName',
  employer: 'organizationName',
  business: 'organizationName',
  'business name': 'organizationName',
  notes: 'notes',
  note: 'notes',
  comment: 'notes',
  comments: 'notes',
  timezone: 'timezone',
  'time zone': 'timezone',
  tz: 'timezone',
  ordo_user_id: 'ordoUserId',
  ordouserid: 'ordoUserId',
  'ordo user id': 'ordoUserId',
  'ordo id': 'ordoUserId',
  ordoid: 'ordoUserId',
}

const CONTACT_FIELDS = new Set([
  'firstName',
  'lastName',
  'email',
  'phoneNumber',
  'organizationName',
  'notes',
  'ordoUserId',
  'timezone',
])

const SKIP_FIELDS = new Set([
  'fullName',
  'schoolLocationId',
  'schoolLocationRole',
  'vendorLocationId',
  'vendorLocationRole',
  'organizationId',
  'organizationRole',
  'schoolLocationsJson',
  'vendorLocationsJson',
  'organizationsJson',
])

export function normalizeHeader(raw: string): string {
  const lower = raw.trim().toLowerCase()
  if (HEADER_ALIASES[lower]) {
    return HEADER_ALIASES[lower]
  }
  const cleaned = lower.replace(/[.#]/g, '').trim()
  if (HEADER_ALIASES[cleaned]) {
    return HEADER_ALIASES[cleaned]
  }
  const camel = lower.replace(/[\s_]+([a-z])/g, (_, c: string) => c.toUpperCase())
  if (HEADER_ALIASES[camel]) {
    return HEADER_ALIASES[camel]
  }
  if (CONTACT_FIELDS.has(camel)) {
    return camel
  }
  return camel
}

// ── CSV text parser ──

export function parseCsvText(text: string): string[][] {
  const input = text.replace(/^\uFEFF/, '')
  const rows: string[][] = []
  let current: string[] = []
  let field = ''
  let inQuotes = false

  for (let i = 0; i < input.length; i++) {
    const ch = input[i]
    if (inQuotes) {
      if (ch === '"') {
        if (input[i + 1] === '"') {
          field += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        field += ch
      }
    } else if (ch === '"') {
      inQuotes = true
    } else if (ch === ',') {
      current.push(field)
      field = ''
    } else if (ch === '\n') {
      current.push(field)
      field = ''
      if (current.some((c) => c.length > 0)) {
        rows.push(current)
      }
      current = []
    } else if (ch !== '\r') {
      field += ch
    }
  }

  if (field || current.length > 0) {
    current.push(field)
    if (current.some((c) => c.length > 0)) {
      rows.push(current)
    }
  }

  return rows
}

// ── Public API ──

export interface CsvParseResult {
  contacts: ParsedContactPreview[]
  originalHeaders: string[]
  mappedHeaders: { original: string; mapped: string; isExtra: boolean }[]
}

export function parseCsvForPreview(csvText: string): CsvParseResult {
  const rows = parseCsvText(csvText)
  if (rows.length < 2) {
    return { contacts: [], originalHeaders: [], mappedHeaders: [] }
  }

  const originalHeaders = rows[0].map((h) => h.trim())
  const normalizedHeaders = originalHeaders.map(normalizeHeader)

  const mappedHeaders = originalHeaders.map((original, i) => {
    const mapped = normalizedHeaders[i]
    const isExtra = !CONTACT_FIELDS.has(mapped) && !SKIP_FIELDS.has(mapped) && mapped !== 'fullName'
    return { original, mapped, isExtra }
  })

  const contacts: ParsedContactPreview[] = []

  for (let r = 1; r < rows.length; r++) {
    const row = rows[r]
    const record: Record<string, string> = {}
    for (let i = 0; i < normalizedHeaders.length; i++) {
      record[normalizedHeaders[i]] = (row[i] ?? '').trim()
    }

    const contact: ParsedContactPreview = {}

    // Handle combined name
    if (record.fullName && !record.firstName && !record.lastName) {
      const parts = record.fullName.trim().split(/\s+/)
      if (parts.length === 1) {
        contact.firstName = parts[0]
      } else {
        contact.firstName = parts.slice(0, -1).join(' ')
        contact.lastName = parts[parts.length - 1]
      }
    }

    // Known fields
    for (const field of CONTACT_FIELDS) {
      const val = record[field]
      if (val) {
        ;(contact as Record<string, string>)[field] = val
      }
    }

    // Extra data
    const extra: Record<string, string> = {}
    for (const [key, val] of Object.entries(record)) {
      if (val && !CONTACT_FIELDS.has(key) && !SKIP_FIELDS.has(key) && key !== 'fullName') {
        // Use the original header name for display
        const headerInfo = mappedHeaders.find((h) => h.mapped === key)
        extra[headerInfo?.original ?? key] = val
      }
    }
    if (Object.keys(extra).length > 0) {
      contact.extraData = extra
    }

    contacts.push(contact)
  }

  return { contacts, originalHeaders, mappedHeaders }
}
